import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { usePlayerStats } from "../hooks/usePlayerStats";
import {
  averageBidValue,
  bidFrequency,
  biddingPointsAverage,
  bidsWon,
  defendingPointsAverage,
  formatAverage,
  formatPercent,
  modeWinRate,
  pointsAverage,
  recentBidForm,
  winRate,
  type PlayerStats,
  EMPTY_PLAYER_STATS,
} from "../types/stats";

/** Sample career for local layout previews (`/stats-demo` in DEV). */
const DEMO_STATS: PlayerStats = {
  ...EMPTY_PLAYER_STATS,
  gamesPlayed: 24,
  gamesWon: 15,
  gamesLost: 9,
  handsPlayed: 186,
  timesBid: 52,
  timesSet: 15,
  bidSuccessRate: (52 - 15) / 52,
  totalPointsScored: 412,
  bestHand: 9,
  bidValueSum: 3 * 20 + 2 * 22 + 4 * 10,
  bidValueCount: 52,
  pointsBidding: 168,
  handsBidding: 78,
  pointsDefending: 244,
  handsDefending: 108,
  soloGamesPlayed: 14,
  soloGamesWon: 9,
  soloGamesLost: 5,
  soloHandsPlayed: 110,
  partnerGamesPlayed: 10,
  partnerGamesWon: 6,
  partnerGamesLost: 4,
  partnerHandsPlayed: 76,
  recentBidOutcomes: [1, 1, 0, 1, 1, 1, 0, 1, 0, 1, 1, 1, 0, 1, 1, 1, 0, 1, 1, 1],
};

export default function StatsPage() {
  const { user, profile } = useAuth();
  const { stats, loading, error } = usePlayerStats(user?.uid);
  const location = useLocation();
  const demo = import.meta.env.DEV && location.pathname === "/stats-demo";
  const displayStats = demo ? DEMO_STATS : stats;
  const displayLoading = demo ? false : loading;
  const displayError = demo ? null : error;

  return (
    <div className="max-w-lg mx-auto px-4 sm:px-6 py-8 sm:py-10">
      <div className="flex items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold">Stats</h1>
        <Link to="/" className="text-sm text-white/60 hover:text-white whitespace-nowrap">
          Back to lobby
        </Link>
      </div>

      <div className="rounded-2xl border border-emerald-400/25 bg-gradient-to-br from-emerald-500/15 to-emerald-900/30 p-5 sm:p-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
        <div className="mb-5">
          <div className="text-xs uppercase tracking-wide text-emerald-200/70 mb-1">Player</div>
          <div className="text-xl sm:text-2xl font-bold truncate">
            {demo ? "Demo Player" : (profile?.screenName ?? "Player")}
          </div>
        </div>

        {displayLoading ? (
          <div className="text-white/60 text-sm py-6 text-center">Loading stats…</div>
        ) : displayError ? (
          <div className="text-red-300 text-sm py-4">{displayError}</div>
        ) : (
          <PlayerStatsBody stats={displayStats} />
        )}
      </div>
    </div>
  );
}

export function PlayerStatsBody({ stats }: { stats: PlayerStats }) {
  const wonBids = bidsWon(stats);
  const freq = bidFrequency(stats);
  const winsPct = winRate(stats);
  const avgBid = averageBidValue(stats);
  const form = recentBidForm(stats);
  const bidPtsAvg = biddingPointsAverage(stats);
  const defPtsAvg = defendingPointsAverage(stats);
  const soloWr = modeWinRate(stats.soloGamesPlayed, stats.soloGamesWon);
  const partnerWr = modeWinRate(stats.partnerGamesPlayed, stats.partnerGamesWon);
  const careerBidWin =
    stats.timesBid <= 0 ? null : stats.bidSuccessRate;

  return (
    <>
      <StatGrid
        cols={3}
        items={[
          { label: "Games", value: String(stats.gamesPlayed) },
          {
            label: "Wins",
            value: String(stats.gamesWon),
            detail: winsPct === null ? undefined : `${formatPercent(winsPct)} win rate`,
          },
          { label: "Losses", value: String(stats.gamesLost) },
        ]}
      />

      <Section title="Bidding">
        <StatGrid
          items={[
            { label: "Hands played", value: String(stats.handsPlayed) },
            { label: "Hands bid", value: String(stats.timesBid) },
            {
              label: "% of hands bid",
              value: formatPercent(freq),
            },
            {
              label: "Bid win %",
              value: formatPercent(careerBidWin),
              detail:
                stats.timesBid <= 0
                  ? undefined
                  : `${wonBids} made · ${stats.timesSet} set`,
            },
            {
              label: "Avg bid",
              value: formatAverage(avgBid),
              detail: avgBid === null ? "Needs new hands" : undefined,
            },
            { label: "Times set", value: String(stats.timesSet) },
          ]}
        />
      </Section>

      <Section title="Recent form">
        <StatGrid
          items={[
            {
              label: "Recent bid win %",
              value: formatPercent(form.successRate),
              detail:
                form.sampleSize <= 0
                  ? "No recent bids yet"
                  : `Last ${form.sampleSize} bids`,
            },
            {
              label: "Recent set %",
              value: formatPercent(form.setRate),
              detail:
                form.sampleSize <= 0
                  ? "No recent bids yet"
                  : `Last ${form.sampleSize} bids`,
            },
          ]}
        />
        {form.sampleSize > 0 ? (
          <FormDots outcomes={stats.recentBidOutcomes} />
        ) : null}
      </Section>

      <Section title="Scoring">
        <StatGrid
          items={[
            { label: "Total points", value: String(stats.totalPointsScored) },
            { label: "Avg / hand", value: formatAverage(pointsAverage(stats)) },
            { label: "Best hand", value: String(stats.bestHand) },
            {
              label: "Avg as bidder",
              value: formatAverage(bidPtsAvg),
              detail:
                stats.handsBidding > 0
                  ? `${stats.pointsBidding} pts · ${stats.handsBidding} hands`
                  : undefined,
            },
            {
              label: "Avg defending",
              value: formatAverage(defPtsAvg),
              detail:
                stats.handsDefending > 0
                  ? `${stats.pointsDefending} pts · ${stats.handsDefending} hands`
                  : undefined,
            },
          ]}
        />
      </Section>

      <Section title="Play modes">
        <StatGrid
          items={[
            {
              label: "Solo games",
              value: String(stats.soloGamesPlayed),
              detail:
                stats.soloGamesPlayed <= 0
                  ? undefined
                  : `${stats.soloGamesWon}–${stats.soloGamesLost}` +
                    (soloWr === null ? "" : ` · ${formatPercent(soloWr)}`),
            },
            {
              label: "Partner games",
              value: String(stats.partnerGamesPlayed),
              detail:
                stats.partnerGamesPlayed <= 0
                  ? undefined
                  : `${stats.partnerGamesWon}–${stats.partnerGamesLost}` +
                    (partnerWr === null ? "" : ` · ${formatPercent(partnerWr)}`),
            },
            {
              label: "Solo hands",
              value: String(stats.soloHandsPlayed),
            },
            {
              label: "Partner hands",
              value: String(stats.partnerHandsPlayed),
            },
          ]}
        />
      </Section>
    </>
  );
}

function FormDots({ outcomes }: { outcomes: number[] }) {
  return (
    <div className="mt-3 flex flex-wrap gap-1" aria-label="Recent bid outcomes">
      {outcomes.map((v, i) => (
        <span
          key={`${i}-${v}`}
          title={v === 1 ? "Made" : "Set"}
          className={
            "h-2 w-2 rounded-sm " +
            (v === 1 ? "bg-emerald-300/80" : "bg-rose-400/70")
          }
        />
      ))}
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mt-5 pt-5 border-t border-white/10">
      <div className="text-xs uppercase tracking-wide text-emerald-200/70 mb-3">{title}</div>
      {children}
    </div>
  );
}

type StatItem = {
  label: string;
  value: string;
  detail?: string;
};

function StatGrid({
  items,
  cols = 2,
}: {
  items: StatItem[];
  cols?: 2 | 3;
}) {
  const gridClass = cols === 3 ? "grid grid-cols-3 gap-2.5 sm:gap-3" : "grid grid-cols-2 gap-2.5 sm:gap-3";
  return (
    <div className={gridClass}>
      {items.map(item => (
        <div
          key={item.label}
          className="rounded-xl border border-white/10 bg-black/15 px-3 py-3 min-w-0"
        >
          <div className="text-[11px] sm:text-xs text-white/50 mb-1 truncate">{item.label}</div>
          <div className="text-lg sm:text-xl font-semibold tabular-nums text-emerald-100 leading-tight">
            {item.value}
          </div>
          {item.detail ? (
            <div className="mt-1 text-[11px] sm:text-xs text-white/45 truncate tabular-nums">
              {item.detail}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}
